// Per-bet manual result: PUT /v1/portal/betslips/:id/manual-result[/exotic|/quaddie|/simple]

export const MANUAL_RESULT_MODES = {
  LEG: "leg",
  EXOTIC: "exotic",
  QUADDIE: "quaddie",
  SIMPLE: "simple",
};

const EXOTIC_TYPES = ["quinella", "exacta", "trifecta", "first4"];
const QUADDIE_TYPES = ["quaddie", "daily-double", "running-double", "treble"];
const SIMPLE_TYPES = ["srm", "sgm", "combo-racing"];
const RESULTABLE_STATUSES = ["active", "approved", "resulted"];

const isGroupSelection = (sel) =>
  ["sgm", "srm"].includes(sel.selectionType) && Array.isArray(sel.selections);

export const hasGroupLegs = (bet) =>
  (bet.selections || []).some(isGroupSelection) ||
  (bet.sgmGroups || []).length > 0 ||
  (bet.srmGroups || []).length > 0;

// Returns null when the bet type has no manual-result endpoint
export const resolveManualResultMode = (bet) => {
  if (!bet || !bet.type) return null;
  if (["single", "multi"].includes(bet.type)) {
    return hasGroupLegs(bet)
      ? MANUAL_RESULT_MODES.SIMPLE
      : MANUAL_RESULT_MODES.LEG;
  }
  if (EXOTIC_TYPES.includes(bet.type)) return MANUAL_RESULT_MODES.EXOTIC;
  if (QUADDIE_TYPES.includes(bet.type)) return MANUAL_RESULT_MODES.QUADDIE;
  if (SIMPLE_TYPES.includes(bet.type)) return MANUAL_RESULT_MODES.SIMPLE;
  return null;
};

export const canManualResultBet = (bet) =>
  !!bet &&
  bet.acceptanceSource !== "bm" &&
  RESULTABLE_STATUSES.includes(bet.status) &&
  !!resolveManualResultMode(bet);

// Single/multi leg (category race | event) -> request value
export const toLegResult = (value, status) => {
  if (status === "cancelled") return "cancel";
  switch (value) {
    case "resulted-win":
    case "Win":
    case "Place":
    case "win":
      return "win";
    case "resulted-loss":
    case "Lose":
    case "Loss":
    case "lose":
      return "lose";
    case "resulted-draw":
    case "Draw":
    case "draw":
      return "draw";
    case "bet-refunded":
    case "bet-cancelled":
    case "Void":
    case "Refunded":
    case "FullyRefunded":
    case "cancel":
      return "cancel";
    default:
      return "pending";
  }
};

// Quaddie runner (category quaddie) -> request value
export const toQuaddieResult = (value, status) => {
  if (status === "cancelled") return "cancel";
  if (value === "Win") return "win";
  if (value === "Lose") return "lose";
  return "pending";
};

export const toBetResult = (value) => {
  switch (value) {
    case "resulted-win":
      return "win";
    case "resulted-loss":
      return "lose";
    case "bet-refunded":
      return "refund";
    default:
      return "pending";
  }
};

// Mirrors the backend rule for single/multi
export const computeLegBetResult = (results) => {
  if (results.includes("lose")) return "lose";
  if (results.includes("pending")) return "pending";
  if (results.includes("win")) return "win";
  if (results.includes("draw")) return "draw";
  return "refund";
};

// Mirrors the backend rule for quaddie/daily-double/running-double/treble,
// cancelled runners are ignored. runners: [{ raceKey, result }]
export const computeQuaddieBetResult = (runners) => {
  const races = {};
  runners.forEach((r) => {
    if (!races[r.raceKey]) races[r.raceKey] = [];
    races[r.raceKey].push(r);
  });
  const raceList = Object.values(races).map((race) =>
    race.filter((r) => r.result !== "cancel")
  );
  if (raceList.some((race) => race.length && race.every((r) => r.result === "lose")))
    return "lose";
  if (raceList.some((race) => !race.length)) return "refund";
  if (raceList.every((race) => race.some((r) => r.result === "win")))
    return "win";
  return "pending";
};

export const isValidFinalOdds = (value) => {
  if (value === null || value === undefined || value === "") return false;
  const str = String(value).trim();
  return /^\d+(\.\d{1,2})?$/.test(str) && Number(str) > 0;
};

const PREVIEW_RESULT_TEXT = {
  "resulted-win": "Win",
  "resulted-loss": "Lose",
  "resulted-draw": "Draw",
  "bet-refunded": "Refund",
  "bet-pending": "Pending (unresulted)",
};

const formatAmount = (value, tokenSymbol) =>
  `${Number(value || 0).toFixed(2)}${tokenSymbol ? ` ${tokenSymbol}` : ""}`;

// Wallet change after the real call; a bonus bet's stake refund goes back to bonus
export const previewWalletChange = (preview) =>
  Number(preview.estPayout || 0) +
  (preview.currencyMethod === "bonus" ? 0 : Number(preview.estStakeRefund || 0)) -
  Number(preview.currentPayout || 0);

// Confirmation popup content from POST .../manual-result[/type]/preview.
// h: the component's $createElement, intro: optional VNode/string shown first
export const buildPreviewConfirmMessage = (h, preview, { betLabel, oddsLabel = "Odds", intro } = {}) => {
  const row = (label, value, opts = {}) =>
    h("div", { class: ["d-flex justify-content-between", opts.class] }, [
      h("span", { class: "text-muted" }, label),
      h("strong", value),
    ]);
  const current = preview.current || {};
  const previous = preview.previous || {};
  const isBonus = preview.currencyMethod === "bonus";
  const token = preview.tokenSymbol;
  const walletChange = previewWalletChange(preview);

  const lines = [];
  if (intro) lines.push(h("div", { class: "mb-50" }, [intro]));
  lines.push(row("Betslip:", betLabel || preview.id));
  lines.push(
    row(
      "Result:",
      `${PREVIEW_RESULT_TEXT[previous.result] || previous.result || "-"} → ${
        PREVIEW_RESULT_TEXT[current.result] || current.result || "-"
      }`
    )
  );
  if (preview.cancelledBy) lines.push(row("Refund reason:", preview.cancelledBy));
  if (preview.finalOdds != null)
    lines.push(row(`${oddsLabel}:`, Number(preview.finalOdds).toFixed(2)));
  if (preview.newStake != null && Number(preview.newStake) !== Number(preview.stake))
    lines.push(
      row("Stake:", `${formatAmount(preview.stake, token)} → ${formatAmount(preview.newStake, token)}`)
    );
  lines.push(row("Payout:", formatAmount(preview.estPayout, token)));
  if (Number(preview.estStakeRefund) > 0)
    lines.push(
      row(
        isBonus ? "Stake refund (to bonus):" : "Stake refund:",
        formatAmount(preview.estStakeRefund, token)
      )
    );
  if (Number(preview.currentPayout) > 0)
    lines.push(
      row("Clawback of previous payout:", `-${formatAmount(preview.currentPayout, token)}`, {
        class: "text-danger",
      })
    );
  lines.push(
    h("hr", { class: "my-50" }),
    row(
      "Customer wallet change:",
      `${walletChange > 0 ? "+" : walletChange < 0 ? "-" : ""}${formatAmount(Math.abs(walletChange), token)}`,
      { class: walletChange < 0 ? "text-danger" : walletChange > 0 ? "text-success" : "" }
    ),
    h(
      "div",
      { class: "text-muted mt-50", style: { fontSize: "12px" } },
      "Funds move immediately. The result may differ if the bet changes before you confirm."
    )
  );
  return h("div", { style: { color: "#1a1a1a", fontSize: "14px" } }, lines);
};
